import { AlertTriangle } from "lucide-react";
import { useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/hooks/useTranslation";

// ─────────────────────────────────────────────
// ConfirmDialog
//
// Destructive confirmation dialog with optional
// text confirmation word input.
// Design: §6.8 Overlays & Feedback, §9.15
// ─────────────────────────────────────────────

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** If provided, user must type this word to enable the confirm button */
  confirmWord?: string;
  onConfirm: () => void;
  isDestructive?: boolean;
  isLoading?: boolean;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel,
  confirmWord,
  onConfirm,
  isDestructive = true,
  isLoading = false,
}: ConfirmDialogProps) {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState("");
  const resolvedConfirmLabel = confirmLabel ?? t("common.confirm");
  const resolvedCancelLabel = cancelLabel ?? t("common.cancel");

  const isConfirmEnabled = confirmWord
    ? inputValue === confirmWord
    : true;

  function handleClose() {
    setInputValue("");
    onOpenChange(false);
  }

  function handleConfirm() {
    if (!isConfirmEnabled || isLoading) return;
    onConfirm();
    setInputValue("");
  }

  return (
    <DialogPrimitive.Root open={open} onOpenChange={handleClose}>
      <DialogPrimitive.Portal>
        {/* Backdrop */}
        <DialogPrimitive.Overlay
          className="fixed inset-0 z-50 animate-fade-in"
          style={{ backgroundColor: "rgba(0,0,0,0.6)" }}
        />

        {/* Modal */}
        <DialogPrimitive.Content
          className={cn(
            "fixed left-1/2 top-1/2 z-50 w-full max-w-sm -translate-x-1/2 -translate-y-1/2",
            "animate-fade-in rounded-card border border-border-default bg-card p-8 shadow-xl",
            "focus:outline-none"
          )}
        >
          {/* Icon */}
          <div
            className={cn(
              "mb-5 flex h-12 w-12 items-center justify-center rounded-full",
              isDestructive ? "bg-danger-bg" : "bg-warning-bg"
            )}
          >
            <AlertTriangle
              className={cn("h-6 w-6", isDestructive ? "text-danger" : "text-warning")}
            />
          </div>

          {/* Title */}
          <DialogPrimitive.Title className="mb-2 text-[18px] font-semibold text-text-primary">
            {title}
          </DialogPrimitive.Title>

          {/* Description */}
          <DialogPrimitive.Description className="mb-5 text-body text-text-secondary">
            {description}
          </DialogPrimitive.Description>

          {/* Optional confirmation input */}
          {confirmWord && (
            <div className="mb-5">
              <label className="mb-1.5 block text-[14px] font-medium text-text-secondary">
                {t("confirmDialog.typePrefix")}{" "}
                <span className="font-mono font-semibold text-danger">
                  {confirmWord}
                </span>{" "}
                {t("confirmDialog.typeSuffix")}
              </label>
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder={confirmWord}
                className="w-full rounded-control border border-border-default bg-bg-input px-3 py-2 text-body text-text-primary placeholder:text-text-muted focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
              />
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={isLoading}
              className="flex-1 rounded-control border border-border-default bg-transparent px-4 py-2 text-[14px] font-medium text-text-secondary transition-colors hover:bg-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              {resolvedCancelLabel}
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={!isConfirmEnabled || isLoading}
              className={cn(
                "flex flex-1 items-center justify-center gap-2 rounded-control px-4 py-2 text-[14px] font-medium text-white transition-opacity",
                isDestructive ? "bg-danger" : "bg-warning",
                (!isConfirmEnabled || isLoading) && "cursor-not-allowed opacity-50"
              )}
            >
              {isLoading && (
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
              )}
              {resolvedConfirmLabel}
            </button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
